import * as React from 'react'
import styled from 'styled-components'

import * as S from '../../../../styles'

import * as select from '../../../../behaviours/select'
import * as hooks from '../../../../hooks'
import { isMobile } from '../../../../utils/matchMediaOnBrowser'

import { SelectButton } from './SelectButton'
import { SelectDrawer } from './SelectDrawer'

export type SelectProps = Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    'value' | 'onChange' | 'placeholder'
> & {
    /** set if needed for a `<label>` */
    id?: string
    className?: string
    borderless?: boolean
    /** currently selected values. Makes the component controlled. Incompatible with `initialSelected`. */
    selected?: any[]
    /** initially selected values. Makes the component uncontrolled. Incompatible with `selected` */
    initialSelected?: any[]
    /** usually a list of `<Option>` */
    children?: React.ReactNode
    /** whether multiselect is allowed. In multiselect, `selected` is an array of any length. In single-select, `selected` is an array of length 0 or 1.
     * @default false */
    multiple?: boolean
    /** handler called when selection changes with currently selected `<Option>`
     * @default () => {} */
    onChange?: (selected: any[]) => void
    /** handler called when dropdown closes with currently selected `<Option>`
     * @default () => {} */
    onClose?: (selected: any[]) => void
    /** handler called when dropdown open with currently selected `<Option>`
     * @default () => {} */
    onOpen?: () => void
    /** placeholder for the `<Select>` element */
    // placeholder?: string
    placeholder?: React.ReactNode
    /** label for the `<SelectButton>` element */
    // label?: string
    label?: (
        selectedOptions: select.OptionData[],
        placeholder: React.ReactNode
    ) => React.ReactNode
    /** placeholder for the filter inside the dropdown (if `filterable` is `true`). */
    filterPlaceholder?: string
    /** maximum height of the dropdown (scroll will be activated beyond) */
    height?: number
    status?: 'default' | 'focused' | 'error'
    /** whether the `<Option>` list is filterable.
     * @default false */
    filterable?: boolean
    alignOptions?: 'left' | 'right'
}

export function SelectComponent(props: SelectProps): React.ReactElement<SelectProps> {
    // eslint-disable-next-line no-undef
    if (process.env.NODE_ENV !== 'production') {
        React.useEffect(() => {
            if (props.selected !== undefined && props.initialSelected !== undefined) {
                console.error(
                    new Error(
                        '<Select> is both controlled and uncontrolled, use either selected or initialSelected'
                    )
                )
            }
        }, [])
    }

    if (props.selected !== undefined) {
        return <ControlledSelect {...props} />
    } else {
        return <UncontrolledSelect {...props} />
    }
}

function UncontrolledSelect({
    initialSelected = [],
    borderless,
    disabled,
    multiple,
    onChange,
    children,
    onClose,
    onOpen,
    className,
    status,
    filterable,
    placeholder,
    label,
    filterPlaceholder,
    height,
    alignOptions,
    id
}: SelectProps): React.ReactElement<SelectProps> {
    let initialSelectedOptions

    if (multiple) {
        initialSelectedOptions = initialSelected
    } else {
        initialSelectedOptions = initialSelected[0] ? [initialSelected[0]] : []
    }

    const [selected, setSelected] = React.useState(initialSelectedOptions)

    function handleChange(selected: select.OptionData[]): void {
        onChange && onChange(selected)
        setSelected(selected.map(option => option.value))
    }

    return (
        <ControlledSelect
            disabled={disabled}
            borderless={borderless}
            selected={selected}
            onChange={handleChange}
            multiple={multiple}
            onClose={onClose}
            onOpen={onOpen}
            className={className}
            id={id}
            filterable={filterable}
            placeholder={placeholder}
            label={label}
            filterPlaceholder={filterPlaceholder}
            status={status}
            height={height}
            alignOptions={alignOptions}
        >
            {children}
        </ControlledSelect>
    )
}

function ControlledSelect({
    selected = [],
    borderless,
    disabled,
    children,
    multiple = false,
    onChange = () => {
        /* noop */
    },
    onClose = () => {
        /* noop */
    },
    onOpen = () => {
        /* noop */
    },
    height,
    placeholder,
    label = defaultLabel,
    id = String(Math.random()),
    className,
    filterable = false,
    filterPlaceholder,
    alignOptions = 'left',
    status
}: SelectProps): React.ReactElement<SelectProps> {
    const [
        { isOpen, selectedOptions, open, close, navigableRegister, deregister, register },
        selectBoxRef,
        scrollRef,
        onFilter,
        filter
    ] = useSelect(selected, onClose, onChange, multiple, filterable)

    function openHandler() {
        onOpen()
        open()
    }

    return (
        <S.form.select.Wrapper
            alignOptions={alignOptions}
            isOpen={isOpen}
            ref={selectBoxRef}
            className={className}
            borderless={borderless}
        >
            <SelectButton
                disabled={disabled}
                isOpen={isOpen}
                id={id}
                onClick={isOpen ? close : openHandler}
                status={status}
                borderless={borderless}
            >
                {label(selectedOptions, placeholder || '')}
            </SelectButton>
            <SelectDrawer
                label={placeholder || ''}
                id={id}
                close={close}
                isOpen={isOpen}
                height={height}
                scrollRef={scrollRef}
                navigableRegister={navigableRegister}
                multiple={multiple}
                deregister={deregister}
                register={register}
                filterable={filterable}
                filter={filter}
                onFilter={onFilter}
                filterPlaceholder={filterPlaceholder}
            >
                {children}
            </SelectDrawer>
        </S.form.select.Wrapper>
    )
}

type SelectSkeletonProps = {
    className?: string
}

function SelectSkeletonComponent({ className }: SelectSkeletonProps) {
    return (
        <SelectSkeletonWrapper className={className}>
            <S.skeleton.Rectangle height={50} />
        </SelectSkeletonWrapper>
    )
}

export const SelectSkeleton = styled(SelectSkeletonComponent)``

const SelectSkeletonWrapper = styled.div`
    margin-bottom: 15px;
    width: 300px;
`

export function useSelect(
    selected: any[],
    onClose: (selected: select.OptionData[]) => void,
    onChange: (selected: select.OptionData[]) => void,
    multiple: boolean,
    filterable: boolean
): [
    select.SelectApi,
    React.RefObject<HTMLDivElement>,
    React.RefObject<HTMLUListElement>,
    (filter: string) => void,
    string
] {
    const selectBoxref = React.useRef<HTMLDivElement>(null)
    const listBoxRef = React.useRef<HTMLUListElement>(null)
    const [filter, setFilter] = React.useState('')
    const [debouncedFilter, setDebouncedFilter] = React.useState('')
    const debouncedUpdateFilter = hooks.useDebounce(setDebouncedFilter, false)
    const selectApi = select.useSelect(
        selected,
        selectBoxref,
        selected => {
            onClose(selected)
            setDebouncedFilter('')
            setFilter('')
        },
        onChange,
        multiple,
        filterable ? debouncedFilter : undefined
    )

    hooks.useLoseFocus(
        selectBoxref,
        () => {
            if (!isMobile() && selectApi.isOpen) {
                selectApi.close()
            }
        },
        [selectApi.isOpen]
    )

    hooks.useScrollLock(isMobile() && selectApi.isOpen)

    const updateFilter = (filter: string): void => {
        setFilter(filter)
        debouncedUpdateFilter(filter)
    }

    return [selectApi, selectBoxref, listBoxRef, updateFilter, filter]
}

export function defaultLabel(
    selectedOptions: select.OptionData[],
    placeholder: string | React.ReactNode
): string | React.ReactNode {
    return selectedOptions.length === 0
        ? placeholder
        : selectedOptions.map(option => option.label).join(', ')
}

export const Select = styled(SelectComponent)<SelectProps>``
